import { Doughnut } from 'react-chartjs-2';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import cn from 'classnames';
import styled from 'styled-components';

import { getChartOptions } from '@/_pages/interwill/components/Dashboard/helpers';
import { WidgetContainer } from '@/features/vfo/components/WidgetContainer';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { BFC } from '@/shared/types';
import { numbers } from '@/shared/utils/numbers';

ChartJS.register(ArcElement, Tooltip, Legend);

export type DoughnutChartItem = {
  id: string;
  label: string;
  value: number;
  color: string;
};

type VfoDoughnutChartProps = {
  title: string;
  items: DoughnutChartItem[];
  type?: 'percent' | 'valueAndPercent';
  legendSeparator?: boolean;
  customTooltipValueDisplay?: (dataValues: number, dataIndex: number) => string | number;
};

export const VfoDoughnutChart: BFC<VfoDoughnutChartProps> = ({
  title,
  items,
  type = 'valueAndPercent',
  legendSeparator,
  customTooltipValueDisplay,
  ...props
}) => {
  const chartData = {
    labels: items.map((item) => item.label),
    datasets: [
      {
        label: title,
        data: items.map((item) => item.value),
        backgroundColor: items.map((item) => item.color),
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  return (
    <WidgetContainerStyled title={title} {...props}>
      <Content>
        <ChartContainer>
          <Doughnut
            data={chartData}
            options={{
              cutout: '50%',
              ...getChartOptions(type, 'doughnut', {
                customTooltipValueDisplay,
                stackBars: true,
              }),
            }}
          />
        </ChartContainer>
        <LegendContainer>
          {items.map((item) => (
            <LegendItem className={cn({ legendSeparator })} key={item.id}>
              <LegendIndicator
                style={{
                  backgroundColor: item.color,
                }}
              />
              <LegendLabel>{item.label}</LegendLabel>
              <LegendValue>
                {type === 'percent'
                  ? numbers.formatPercent({
                      value: item.value,
                      withSign: true,
                    })
                  : item.value}
              </LegendValue>
            </LegendItem>
          ))}
        </LegendContainer>
      </Content>
    </WidgetContainerStyled>
  );
};

const Content = styled.div`
  display: flex;
  gap: 16px;
  height: 100%;
  align-items: center;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  max-width: 138px;
  max-height: 138px;
  min-width: 125px;
  min-height: 125px;

  @container ${breakpoints.sm} {
    max-width: 125px;
    max-height: 125px;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  &.legendSeparator {
    border-bottom: 1px solid ${COLORS.systemDark100};
    padding: 6px 0 6px 12px;

    &:last-of-type {
      border: none;
    }
  }
`;

const LegendIndicator = styled.div`
  width: 6px;
  height: 16px;
  border-radius: 100px;
  flex-shrink: 0;
`;

const LegendValue = styled.span`
  color: ${COLORS.systemDark300};
  line-height: 20px;
`;

const LegendLabel = styled(LegendValue)`
  flex: 1;
`;

const WidgetContainerStyled = styled(WidgetContainer)`
  flex: 5;

  ${media.md}, ${breakpoints.sm} {
    padding: 12px;
  }
`;
